import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, ListChecks, FileText, FileSpreadsheet } from 'lucide-react';
import { money } from '../lib/units';
import { irrBom, irrBomPdf, irrBomXLSX, BOM_SECS, bomQty } from '../lib/irrBom';
import { portalRoot } from './TourGuide';
import { offerPdf, offerTotals } from '../lib/offerPdf';
import DecIn from './DecIn';

export const openIrrBom = () => window.dispatchEvent(new Event('giss-irr-bom'));

// bill of materials of the drawn irrigation (pipes per Ø, sprinklers, valves, connectors) + PDF / Excel
function OfferBox({ t, project, updateProject }) {
  const o = project.offer || {}, set = (p) => updateProject((pr) => ({ ...pr, offer: { ...(pr.offer || {}), ...p } })), tot = offerTotals(project, t);
  return (
    <div className="lv-box" data-testid="offer-box">
      <div className="sub-title"><FileText size={14} /> {t.ofTitle}</div>
      <label className="pit-num"><span>{t.ofNo}</span><input className="offer-in" data-testid="offer-no-input" value={o.no || ''} placeholder="ANG-…" onChange={(e) => set({ no: e.target.value })} /></label>
      <label className="pit-num"><span>{t.ofCustomer}</span><input className="offer-in" data-testid="offer-customer-input" value={o.customer || ''} onChange={(e) => set({ customer: e.target.value })} /></label>
      <div className="mc-grid">
        <label className="pit-num"><span>{t.ofHours}</span><DecIn data-testid="offer-hours-input" value={o.hours} onNum={(n) => set({ hours: n })} /></label>
        <label className="pit-num"><span>{t.ofRate}</span><DecIn data-testid="offer-rate-input" value={o.rate} onNum={(n) => set({ rate: n })} /></label>
      </div>
      <div className="bom-total" data-testid="offer-total">{t.ofLabourSec}: <b>{money(tot.lab)}</b> · {t.vatNet}: <b>{money(tot.net)}</b> · {t.ofGross}: <b data-testid="offer-gross">{money(tot.gross)}</b></div>
      <button className="btn btn-green btn-sm" data-testid="offer-pdf-btn" onClick={() => offerPdf(project, t)}><FileText size={14} /> {t.ofPdf}</button>
    </div>
  );
}

export default function IrrBomDialog({ t, project, updateProject }) {
  const [on, setOn] = useState(false);
  useEffect(() => { const h = () => setOn(true); window.addEventListener('giss-irr-bom', h); return () => window.removeEventListener('giss-irr-bom', h); }, []);
  if (!on) return null;
  const { grp, total } = irrBom(project, t), empty = !BOM_SECS.some((s) => grp[s].length);
  return createPortal(
    <div className="editor-backdrop" data-testid="irr-bom-dialog">
      <div className="editor irr-bom">
        <div className="editor-head"><h4><ListChecks size={18} /> {t.bomTitle}</h4><button className="icon-btn" data-testid="irr-bom-close-btn" onClick={() => setOn(false)}><X size={16} /></button></div>
        {empty ? <p className="empty" data-testid="irr-bom-empty">{t.bomEmpty}</p> : BOM_SECS.filter((s) => grp[s].length).map((s) => (
          <div key={s} className="bom-sec" data-testid={`irr-bom-sec-${s}`}>
            <b>{t['bomSec_' + s]}</b>
            {grp[s].map((r, i) => (
              <div key={r.key} className="bom-row" data-testid={`irr-bom-row-${s}-${i}`}>
                <span>{r.label}{r.auto && <em> · {t.bomAuto}</em>}</span>
                <b className="mono">{bomQty(r, t)}</b>
                <span className="mono">{r.amount != null ? money(r.amount) : '–'}</span>
              </div>
            ))}
          </div>
        ))}
        <div className="bom-total" data-testid="irr-bom-total">{t.vatNet}: <b>{money(total)}</b></div>
        <p className="muted small-hint">{t.bomAutoHint}</p>
        {!empty && updateProject && <OfferBox t={t} project={project} updateProject={updateProject} />}
        <div className="row">
          <button className="btn btn-dark btn-sm" data-testid="irr-bom-pdf-btn" disabled={empty} onClick={() => irrBomPdf(project, t)}><FileText size={14} /> PDF</button>
          <button className="btn btn-green btn-sm" data-testid="irr-bom-xlsx-btn" disabled={empty} onClick={() => irrBomXLSX(project, t)}><FileSpreadsheet size={14} /> Excel</button>
        </div>
      </div>
    </div>,
    portalRoot(),
  );
}
